import { assign, get, find, isEmpty, toString } from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  FormControl,
  FormGroup,
  Input,
  InputLabel,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import styles from './Locations.module.css';
import WideModal from '../ReusableComponents/WideModal';
import LocationHours from './LocationHours';

const useStyles = makeStyles()((theme) => ({
  save: {
    background: get(theme, 'palette.success.main'),
    '&:hover': {
      background: get(theme, 'palette.success.dark'),
    },
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'row',
  },
  formControl: {
    flex: '1',
    marginRight: '5px',
    marginBottom: '5px',
  },
}));

const isValidHour = (hours, hour) =>
  !find(
    hours,
    ({ start, end }) =>
      (start < hour.start && hour.start < end) ||
      (start < hour.end && hour.end < end) ||
      (hour.start < start && start < hour.end) ||
      (hour.start < end && end < hour.end),
  );

const findLocationErrors = (location) => {
  if (isEmpty(location.name)) return 'Please enter a name for the location.';

  const invalidHours = !isEmpty(find(location.hours, (h) => h.start >= h.end));
  if (invalidHours) return 'Please make sure start times are before end times';

  const findOverLappingHours = !isEmpty(
    find(location.hours, (hour, i) => {
      const otherHours = location.hours.filter((_, index) => i !== index);
      return !isValidHour(otherHours, hour);
    }),
  );
  if (findOverLappingHours) return 'Please make sure no hours overlap';

  if (location.min_booking_days > location.max_booking_days) {
    return 'Max booking days must be greater than min booking days';
  }

  return null;
};

export default function LocationModal(props) {
  const { classes } = useStyles();
  const { onClose, location: pLocation = {}, saveLocation } = props;
  const [location, updateLocation] = useState(assign({}, pLocation));
  const [errors, setErrors] = useState(null);

  const setLocation = ({ target: { value: name } }) => {
    const update = assign({}, location, { name });
    updateLocation(update);
  };

  const setLimit = ({ target: { value, name } }) => {
    if (value < 0) return;

    const update = assign({}, location, { [name]: value });
    updateLocation(update);
  };

  const save = () => {
    const locationErrors = findLocationErrors(location);

    if (locationErrors) {
      setErrors(locationErrors);
    } else {
      saveLocation(location);
    }
  };

  return (
    <WideModal onClose={onClose} open>
      <div className={styles.locationModalContainer}>
        <div className={styles.locationModalInputs}>
          <FormGroup className={classes.formGroup}>
            <FormControl variant="standard" className={classes.formControl}>
              <InputLabel htmlFor="location-modal-location-name">
                Location
              </InputLabel>
              <Input
                id="location-model-location-name"
                onChange={setLocation}
                value={location.name || ''}
                name="name"
                type="text"
              />
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
              <InputLabel htmlFor="location-modal-max-units-input">
                Daily Limits (Units):
              </InputLabel>
              <Input
                id="location-modal-max-units-input"
                type="number"
                name="max_units_per_day"
                onChange={setLimit}
                value={toString(location.max_units_per_day)}
              />
            </FormControl>
          </FormGroup>
          <FormGroup className={classes.formGroup}>
            <FormControl variant="standard" className={classes.formControl}>
              <InputLabel htmlFor="location-modal-max-boxes-input">
                Daily Limits (Boxes):
              </InputLabel>
              <Input
                id="location-modal-max-boxes-input"
                type="number"
                name="max_boxes_per_day"
                onChange={setLimit}
                value={toString(location.max_boxes_per_day)}
              />
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
              <InputLabel htmlFor="location-modal-max-vendors-per-slot-input">
                Daily Limit per Vendor (# of Units):
              </InputLabel>
              <Input
                id="location-modal-max-vendors-per-slot-input"
                type="number"
                name="max_units_per_vendor_per_day"
                onChange={setLimit}
                value={toString(location.max_units_per_vendor_per_day)}
              />
            </FormControl>
          </FormGroup>

          <FormGroup className={classes.formGroup}>
            <FormControl variant="standard" className={classes.formControl}>
              <InputLabel htmlFor="location-modal-max-boxes-per-slot-input">
                Time Slot Limit (# of Boxes):
              </InputLabel>
              <Input
                id="location-modal-max-boxes-per-slot-input"
                type="number"
                name="max_boxes_per_slot"
                onChange={setLimit}
                value={toString(location.max_boxes_per_slot)}
              />
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
              <InputLabel htmlFor="location-modal-max-vendors-per-slot">
                Max Vendors Per Slot:
              </InputLabel>
              <Input
                id="location-modal-max-vendors-per-slot"
                type="number"
                name="max_vendors_per_slot"
                onChange={setLimit}
                value={toString(location.max_vendors_per_slot)}
              />
            </FormControl>
          </FormGroup>

          <FormGroup className={classes.formGroup}>
            <FormControl variant="standard" className={classes.formControl}>
              <InputLabel htmlFor="location-modal-min-booking-days">
                Min Booking Days:
              </InputLabel>
              <Input
                id="location-modal-min-booking-days"
                type="number"
                name="min_booking_days"
                onChange={setLimit}
                value={toString(location.min_booking_days)}
              />
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
              <InputLabel htmlFor="location-modal-max-booking-days">
                Max Booking Days:
              </InputLabel>
              <Input
                id="location-modal-max-booking-days"
                type="number"
                name="max_booking_days"
                onChange={setLimit}
                value={toString(location.max_booking_days)}
              />
            </FormControl>
          </FormGroup>
        </div>

        <LocationHours
          hours={location.hours || []}
          setHours={(hours) => {
            const update = assign({}, location, { hours });
            updateLocation(update);
          }}
        />

        <div className={styles.errors}>{errors}</div>

        <div className={styles.locationModalActions}>
          <Button
            onClick={save}
            className={classes.save}
            color="primary"
            variant="contained"
          >
            {`${location.id ? 'Save' : 'Create'} Location`}
          </Button>
          <Button variant="contained" onClick={onClose}>
            {location.id ? 'Close' : 'Cancel'}
          </Button>
        </div>
      </div>
    </WideModal>
  );
}

LocationModal.propTypes = {
  location: PropTypes.shape({}),
  onClose: PropTypes.func.isRequired,
  saveLocation: PropTypes.func.isRequired,
};
